import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import router from 'src/router';
import { G2S } from 'core/g2s';
import * as api from 'core/api';
import * as utils from 'core/utils';
import * as flights from 'core/flights';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component
export default class FlightsBookingPassengersFormComponent extends Vue {
    @Prop()
    flightBookingDetails: api.FlightBookingDetails;

    @Prop()
    reservation: flights.ReservationModel;

    @Prop()
    countries: api.Destination[];

    @Prop()
    passenger: flights.PassengerModel;

    @Prop()
    gaParams: utils.GoogleAnalyticsParams;

    @Prop()
    gaAlreadySubmitted: string[];

    @Prop()
    searchResultsUrl: string;

    @Prop()
    v: any;

    dateMask: string | null = null;

    isDateChangedInForm = false;

    passengerBirthday: string | null = null;

    passengerDocumentValidTill: string | null = null;

    passengerGender: flights.Gender | null = null;

    passengerCitizenship: string | null = null;

    recommendedCountries: any[] = [];

    sortedCountries: api.Destination[] = [];

    searchCitizenship: string = '';

    tooltipActivator: string | null = null;
    tooltipText: string | null = null;
    tooltipVisible: boolean = false;
    tooltipTimeout: number | null = null;

    mounted() {
        this.dateMask = moment().localeData().longDateFormat('L').replace(/(D|M|Y)/gi, '#');

        if (this.passenger.birthday) {
            this.passengerBirthday = moment(this.passenger.birthday, 'YYYY-MM-DD').format('L');
        }

        if (this.passenger.citizenship) {
            this.passengerCitizenship = this.passenger.citizenship.toString();
        }

        if (this.passenger.gender) {
            this.passengerGender = flights.Gender[this.passenger.gender];
        }

        if (this.passenger.documentValidTill) {
            this.passengerDocumentValidTill = moment(this.passenger.documentValidTill, 'YYYY-MM-DD').format('L');
        }
        
        if (this.flightBookingDetails.countryCrossing) {
            this.passenger.checkDocType(this.flightBookingDetails.countryCrossing, this.passenger.citizenship, this.passenger.type);
        }

        this.sortedCountries = [...this.countries];

        if (this.sortedCountries) {
            this.sortedCountries.sort((a, b) => a.localizedName == b.localizedName ? 0 : (a.localizedName > b.localizedName ? 1 : -1));
        }
    }

    get genderOptions() {
        return [
            {
                label: i18next.t('label_gender_' + flights.Gender[flights.Gender.Male].toLowerCase()),
                value: flights.Gender.Male
            },
            {
                label: i18next.t('label_gender_' + flights.Gender[flights.Gender.Female].toLowerCase()),
                value: flights.Gender.Female
            }
        ];
    }

    get selectCountries() {
        return this.recommendedOrSearchCountries.map((country) => {
            if (!country.header) {
                return {
                    label: country.localizedName,
                    value: country.uniqueId
                };
            }
            return country;
        });
    }

    get recommendedOrSearchCountries() {
        const iataCodes = [G2S.config.marketCode.toLowerCase()];
        if (this.flightBookingDetails.destinations) {
            Object.values(this.flightBookingDetails.destinations).forEach((destination) => {
                iataCodes.push(destination.country.iataCode.toLowerCase());
            });
        }

        const recommendedCountries: any = this.sortedCountries.filter((counrty) => utils.uniqueArray(iataCodes).includes(counrty.iataCode.toLowerCase()));

        return recommendedCountries.length && !this.searchCitizenship ? [
            { header: i18next.t('label_recommended') },
            ...recommendedCountries,
            { header: i18next.t('label_alphabet_order') },
            ...this.sortedCountries
        ] : this.sortedCountries.filter((country: api.Destination) => country.localizedName.toLowerCase().includes(this.searchCitizenship.toLowerCase()));
    }

    get passengerCardAirlineOptions() {
        if (this.flightBookingDetails.partnerAirlines
            && this.flightBookingDetails.partnerAirlines.length) {
            return this.flightBookingDetails.partnerAirlines;
        } else {
            return [];
        }
    }

    get hasPassengerCardAirlineOptions() {
        const hasOptions = this.passengerCardAirlineOptions && this.passengerCardAirlineOptions.length;
        if (!this.gaAlreadySubmitted.includes('flights.booking.step1.card.miles.display') && hasOptions) {
            (<any>this).$ga.sendEvent('flights.booking',
                `step1.card.miles.display`,
                `flights.booking.step1.card.miles.display`,
                this.gaParams
            );
            this.gaAlreadySubmitted.push('flights.booking.step1.card.miles.display');
        }
        return hasOptions;
    }

    get passengerCardAirline() {
        return this.passengerCardAirlineOptions.find(
            airlineOption => airlineOption.uniqueId == this.passenger.cardAirlineId
        );
    }

    @Watch('passenger.birthday')
    onPassengerBirthdayChanged(val: string) {
        if (!this.isDateChangedInForm) {
            if (val) {
                this.passengerBirthday = moment(val, 'YYYY-MM-DD').format('L');
            } else {
                this.passengerBirthday = null;
            }
        }
        this.isDateChangedInForm = false;
    }

    @Watch('passenger.citizenship')
    onPassengerCitizenshipChanged(val: string) {
        if (val) {
            this.passengerCitizenship = val.toString();
        }
    }

    @Watch('passenger.documentValidTill')
    onPassengerDocumentValidTillChanged(val: string) {
        if (!this.isDateChangedInForm) {
            if (val) {
                this.passengerDocumentValidTill = moment(val, 'YYYY-MM-DD').format('L');
            } else {
                this.passengerDocumentValidTill = null;
            }
        }
        this.isDateChangedInForm = false;
    }

    @Watch('passenger.gender')
    onPassengerGenderChanged(val: string) {
        if (val) {
            if (val.toLowerCase() === 'male') {
                this.passengerGender = flights.Gender.Male;
            }
            else if (val.toLowerCase() === 'female') {
                this.passengerGender = flights.Gender.Female;
            }
        }
    }

    onClickCitizenship() {
        setTimeout(() => (<any>this.$refs).passengerCitizenshipSearch.focus(), 100);
    }

    onPassengerFormInputBlur() {
        this.$emit('passenger-form-blur');
    }

    onPassengerBirthdayChange(v) {
        this.isDateChangedInForm = true;

        if (v) {
            const m = moment(v, moment().localeData().longDateFormat('L'), true);

            if (m.isValid()) {
                this.passenger.birthday = m.format('YYYY-MM-DD');
            } else {
                this.passenger.birthday = m.format('YYYY-MM-DD');
            }
        } else {
            this.passenger.birthday = '';
        }
    }

    onPassengerCardChange() {
        this.passenger.card = utils.String.normalizeMilesCard(this.passenger.card);

        this.$emit('change');
    }

    onPassengerCitizenshipChange(value) {
        this.passenger.citizenship = parseInt(value);
        this.passenger.checkDocType(this.flightBookingDetails.countryCrossing, this.passenger.citizenship, this.passenger.type);

        if (!this.passenger.documentNumber) {
            (<any>this.$refs).passengerDocumentNumber.focus();
        }
        if (!this.v.citizenship.isCitizenshipAccept) {
            (<any>this.$refs).citizenshipError.isActive = true;
            (<any>this).$ga.sendEvent('flights.booking', 'msg.not.nationality', 'flights.booking.msg.not.nationality', this.gaParams);
        }
        
        this.$emit('change');
    }

    onCloseCitizenshipErrorClick() {
        (<any>this).$ga.sendEvent('flights.booking', 'msg.not.nationality', 'flights.booking.msg.not.nationality.x', this.gaParams);
        (<any>this.$refs).citizenshipError.isActive = false;
    }

    onSearchResults () {
        (<any>this).$ga.sendEvent('flights.booking', 'msg.not.nationality', 'flights.booking.msg.not.nationality.search', this.gaParams);
        setTimeout(() => router.push(this.searchResultsUrl), 500);
    }

    onPassengerDocumentNumberChange() {
        this.$emit('change');
    }

    onPassengerDocumentNumberInput() {
        this.passenger.checkDocType(this.flightBookingDetails.countryCrossing, this.passenger.citizenship, this.passenger.type);

        this.passenger.documentNumber = utils.String.normalizeDocumentNumber(
            this.passenger.documentNumber,
            this.passenger.validationConstraints.allowCyrillicDocumentNumber,
            () => {
                this.showTooltip(
                    (<any>this.$refs).passengerDocumentNumber.$el,
                    i18next.t('error_letters_' + (this.passenger.validationConstraints.allowCyrillicDocumentNumber ? 'numbers_only' : 'latin_numbers_only'))
                );
            }
        );
        (<any>this).v.middleName.$touch();
        // Hack to render new value
        (<any>this.$refs).passengerDocumentNumber.lazyValue = this.passenger.documentNumber;
    }

    onPassengerDocumentValidTillChange(v) {
        this.isDateChangedInForm = true;

        if (v) {
            const m = moment(v, moment().localeData().longDateFormat('L'), true);

            if (m.isValid()) {
                this.passenger.documentValidTill = m.format('YYYY-MM-DD');
            } else {
                this.passenger.documentValidTill = m.format('YYYY-MM-DD');
            }
        } else {
            this.passenger.documentValidTill = '';
        }
    }

    onPassengerGenderChange(value) {
        this.passenger.gender = flights.Gender[value];

        if (!this.passenger.lastName) {
            (<any>this.$refs).passengerLastName.focus();
        }

        if (this.passenger.gender) {
            this.$emit('change');
        }
    }

    onPassengerFirstNameChange() {
        this.$emit('change');
    }

    onPassengerFirstNameInput() {
        this.passenger.firstName = utils.String.normalizePassengerName(
            this.passenger.firstName,
            this.passenger.allowCyrillicName,
            () => {
                this.showTooltip(
                    (<any>this.$refs).passengerFirstName.$el,
                    i18next.t('error_letters_' + (this.passenger.allowCyrillicName ? 'only' : 'latin_only'))
                );
            },
            true
        );
        // Hack to render new value
        (<any>this.$refs).passengerFirstName.lazyValue = this.passenger.firstName;
    }

    onPassengerLastNameChange() {
        this.$emit('change');
    }

    onPassengerLastNameInput() {
        this.passenger.lastName = utils.String.normalizePassengerName(
            this.passenger.lastName,
            this.passenger.allowCyrillicName,
            () => {
                this.showTooltip(
                    (<any>this.$refs).passengerLastName.$el,
                    i18next.t('error_letters_' + (this.passenger.allowCyrillicName ? 'only' : 'latin_only'))
                );
            },
            true
        );
        // Hack to render new value
        (<any>this.$refs).passengerLastName.lazyValue = this.passenger.lastName;
    }

    onPassengerMiddleNameChange() {
        this.$emit('change');
    }

    onPassengerMiddleNameInput() {
        this.passenger.middleName = utils.String.normalizePassengerLastName(
            this.passenger.middleName,
            this.passenger.allowCyrillicName,
            () => {
                this.showTooltip(
                    (<any>this.$refs).passengerMiddleName.$el,
                    i18next.t('error_letters_' + (this.passenger.allowCyrillicName ? 'only' : 'latin_only'))
                );
            }
        );
        // Hack to render new value
        (<any>this.$refs).passengerMiddleName.lazyValue = this.passenger.middleName;
    }

    showTooltip(target, text) {
        (<any>this).$refs.tooltip.isActive = false;

        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
        }

        this.$nextTick(() => {
            this.tooltipText = text;
            this.tooltipActivator = target;
            (<any>this).$refs.tooltip.isActive = true;

            this.tooltipTimeout = window.setTimeout(() => {
                (<any>this).$refs.tooltip.isActive = false;
            }, 2000);
        });
    }
}
